import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAttachment,
  deleteAttachment,
  getDraftUploadUrl,
  getTargetAttachments,
  uploadFileToPresignedUrl,
} from '../../lib/api/attachments';
import {
  getAllowedAttachmentMimeTypes,
  isSecretAttachmentFileName,
  maxAttachmentSizeBytes,
  normalizeAttachmentMimeType,
  type AttachmentTargetType,
  type PendingAttachmentDraft,
} from '../../types/attachment';
import { queryKeys } from './queryKeys';

export const validateAttachmentFile = (file: File, targetType?: AttachmentTargetType) => {
  const mimeType = normalizeAttachmentMimeType(file, targetType);
  if (isSecretAttachmentFileName(file.name) || !getAllowedAttachmentMimeTypes(targetType).includes(mimeType)) {
    throw new Error('Unsupported file type.');
  }

  if (file.size > maxAttachmentSizeBytes) {
    throw new Error('File must be 10MB or smaller.');
  }

  return mimeType;
};

/**
 * 대상 엔티티(daemonTrigger/codeReview/completionReport/document)의 첨부 목록을 조회한다.
 * targetType/targetId 중 하나라도 비면 비활성(enabled=false).
 */
export const useTargetAttachments = (
  token: string | null,
  targetType: AttachmentTargetType | null,
  targetId: string | null,
) => {
  return useQuery({
    queryKey: queryKeys.attachments.target(targetType ?? '', targetId ?? ''),
    queryFn: () => getTargetAttachments(token as string, targetType as AttachmentTargetType, targetId as string),
    enabled: Boolean(token) && Boolean(targetType) && Boolean(targetId),
    staleTime: 30_000,
  });
};

export const useAttachments = (token: string | null, daemonTriggerId: string | null) =>
  useTargetAttachments(token, daemonTriggerId ? 'daemonTrigger' : null, daemonTriggerId);

/**
 * Draft 업로드 — trigger 생성 전에 R2에 직접 PUT 업로드.
 * 결과의 key/메타데이터를 메모리에 보관했다가 trigger 생성 시 attachmentDrafts로 전달한다.
 */
export const useUploadDraftAttachment = (token: string | null, targetType?: AttachmentTargetType) => {
  return useMutation({
    mutationFn: async (input: {
      file: File;
      onProgress?: (progress: number) => void;
    }): Promise<PendingAttachmentDraft> => {
      const contentType = validateAttachmentFile(input.file, targetType);
      const { uploadUrl, key } = await getDraftUploadUrl(token as string, {
        fileName: input.file.name,
        contentType,
        size: input.file.size,
        ...(targetType ? { targetType } : {}),
      });
      await uploadFileToPresignedUrl(uploadUrl, input.file, input.onProgress, contentType);
      return {
        key,
        originalName: input.file.name,
        mimeType: contentType,
        size: input.file.size,
      };
    },
  });
};

export const useAttachTargetAttachment = (token: string | null, targetType: AttachmentTargetType, targetId: string) => {
  const queryClient = useQueryClient();
  const uploadDraftMutation = useUploadDraftAttachment(token, targetType);

  return useMutation({
    mutationFn: async (input: { file: File; onProgress?: (progress: number) => void }) => {
      const draft = await uploadDraftMutation.mutateAsync(input);
      return createAttachment(token as string, {
        targetType,
        targetId,
        key: draft.key,
        originalName: draft.originalName,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.attachments.target(targetType, targetId),
      });
    },
  });
};

export const useDeleteAttachment = (token: string | null, targetType: AttachmentTargetType, targetId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAttachment(token as string, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.attachments.target(targetType, targetId),
      });
    },
  });
};
